import React from 'react';
import Style from './sessionsGenerator.module.css';
import { useTranslation } from 'react-i18next';
import { Card, Message, Button, WeezHeader, ToastContext } from '@weezevent/nacre';
import { useForm } from '../../../utils/validators';
import DatesGeneratorForm from './components/datesGeneratorForm';
import useAppContext from '../../../context/useAppContext';
import SettingsForm from './components/settingsForm';
import RatesForm from './components/ratesForm';
import { useHistory } from 'react-router-dom';
import SessionModel from '../../../models/sessionModel';
import RatesTreeModel from '../../../models/ratesTreeModel';
import { formatSessionDate } from '../../attendee/list/list';
import i18n from 'i18next';

export default function SessionsGenerator() {
    const { t } = useTranslation();
    const locale = i18n.language;

    const [{ event, organization, event_timezone }] = useAppContext();
    const history = useHistory();
    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const [errorsSlot, setErrorsSlot] = React.useState([]);

    const toast = React.useContext(ToastContext);

    const validateStartDate = React.useCallback((name, date, form) => {
        if (!form.start_date) {
            return t('ticket.sessions.create.dates.error.startDate');
        }

        return true;
    }, []);

    const validateEndDate = React.useCallback((name, date, form) => {
        if (form.repeat_mode && !form.end_date) {
            return t('ticket.sessions.create.dates.error.endDate');
        }

        if (!form.start_date) {
            return true;
        }

        if (form.end_date && form.start_date) {
            if (form.end_date <= form.start_date) {
                return t('ticket.sessions.create.dates.error.beforeStartDate');
            }
        }
        return true;
    }, []);

    const validateDaysWeek = React.useCallback((name, date, form) => {
        if (form.repeat_mode && !form.days_of_week.length) {
            return t('ticket.sessions.create.dates.error.daysWeek');
        }

        return true;
    }, []);

    const validateRates = React.useCallback((name, date, form) => {
        if (!Object.keys(form.rates).length) {
            return t('ticket.sessions.create.dates.error.rates');
        }

        return true;
    }, []);

    const [form, setForm, setFormObject, formErrors, formValidate, setErrors] = useForm(
        {
            start_date: null,
            slots: [{ id: 0, start: '10:00' }],
            repeat_mode: null,
            end_date: null,
            days_of_week: [],
            rates: {},
            rate_groups: {},
            enabled: true,
            quota: null,
            tax_id: null,
            timezone: event_timezone
        },
        {
            start_date: validateStartDate,
            end_date: validateEndDate,
            days_of_week: validateDaysWeek,
            rates: validateRates
        }
    );

    const onLoadRatesForm = React.useCallback(
        rates => {
            let selectedRates = {};
            //Preselect all rates
            rates.rates_list.forEach(rate => {
                selectedRates[rate.id] = null;
            });
            setFormObject({ rates: selectedRates }, true);
        },
        [setFormObject]
    );

    const [rates, loadingRates] = RatesTreeModel.useApiModel(
        {
            organizationId: organization.pk,
            eventId: event.pk,
            onSuccess: response => onLoadRatesForm(response)
        },
        [organization, event, onLoadRatesForm]
    );

    const onChangeForm = React.useCallback(
        (name, value) => {
            setForm(name, value);
            let _errors = formErrors;
            delete _errors[name];
            setErrors(_errors);
        },
        [formValidate, form, setForm, formErrors]
    );

    const onChangeFormObject = React.useCallback(
        (partialForm, withMerge) => {
            setFormObject(partialForm, withMerge);
            let _errors = formErrors;
            Object.keys(partialForm).forEach(key => {
                delete _errors[key];
            });
            setErrors(_errors);
        },
        [formValidate, form, setForm, formErrors]
    );

    const disabledSubmit = React.useMemo(() => {
        return Object.keys(formErrors).length || isSubmitting || errorsSlot.length;
    }, [formErrors, isSubmitting, errorsSlot]);

    const messageErrorSubmit = React.useCallback(
        response => {
            if (response.count_dates_already_exists && Number(response.count_dates_already_exists) > 0 && Array.isArray(response.sessions_dates_already_exists)) {
                let count = Number(response.count_dates_already_exists);
                if (count <= 5) {
                    let _arrayDates = response.sessions_dates_already_exists.reduce((acc, isostring) => {
                        acc.push(formatSessionDate(isostring, event_timezone, locale));

                        return acc;
                    }, []);
                    return t('ticket.sessions.actions.generate_sessions.errorDuplicatedDates', { count: count, dates: _arrayDates.join(', ') });
                }

                return t('ticket.sessions.actions.generate_sessions.errorDuplicatedDatesGeneric', { count: count });
            }

            return t(`ticket.sessions.actions.generate_sessions.error`);
        },
        [locale, event_timezone, t]
    );

    const handleSubmit = React.useCallback(() => {
        if (!formValidate()) {
            return;
        }

        setIsSubmitting(true);
        SessionModel.perform_action(
            'generate_sessions',
            { ...form },
            {
                organization,
                event
            }
        )
            .then(response => {
                toast.success(t(`ticket.sessions.actions.generate_sessions.success`, { count: response.response }));
                history.push(`/ticket/O${organization.pk}/E${event.pk}/settings/sessions`);
            })
            .catch(e => {
                let res = {};
                if (e.code === 400 && e.response) {
                    res = e.response;
                }
                console.error(e);
                toast.error(messageErrorSubmit(res));
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    }, [formValidate, toast, messageErrorSubmit]);

    return (
        <div className={Style['session-create']}>
            <WeezHeader
                title={t('ticket.sessions.create.title')}
                backLink={{ onClick: () => history.push(`/ticket/O${organization.pk}/E${event.pk}/settings/sessions`) }}
                rightComponent={[
                    <Button primary key={'submit'} onClick={() => handleSubmit()} label={t(`ticket.common.action.save`)} disabled={disabledSubmit} loading={isSubmitting} />
                ]}
            />
            <Card.FormManager useSteps={false}>
                <Card.FormStep
                    title={t(`ticket.sessions.create.dates.title`)}
                    rightComponent={
                        <Message type="tips" title={t('ticket.common.tips')} mini>
                            {t('ticket.sessions.create.dates.tips')}
                        </Message>
                    }
                >
                    <DatesGeneratorForm
                        errors={formErrors}
                        errorsSlot={errorsSlot}
                        onChangeErrorsSlot={setErrorsSlot}
                        form={form}
                        onChangeForm={onChangeForm}
                        timezone={event_timezone}
                        onChangeFormObject={onChangeFormObject}
                    />
                </Card.FormStep>
                <Card.FormStep title={t(`ticket.sessions.detail.settings.title`)} rightComponent>
                    <SettingsForm form={form} onChangeForm={onChangeForm} />
                </Card.FormStep>
                <Card.FormStep
                    className={Style['rates-form']}
                    title={
                        <>
                            <div>{t(`ticket.sessions.detail.rates.title`)}</div>
                        </>
                    }
                    rightComponent={
                        <Message type="tips" title={t('ticket.common.tips')} mini>
                            {t('ticket.sessions.detail.rates.tips')}
                        </Message>
                    }
                >
                    <RatesForm rates={rates} loadingRates={loadingRates} form={form} onChangeForm={onChangeForm} errors={formErrors} />
                </Card.FormStep>
            </Card.FormManager>
        </div>
    );
}
